/**
 * Checkout links opened in a new tab or window (D254).
 *
 * A Peak checkout cannot be created before a new tab opens: the handoff is asynchronous
 * (`cart.js`, then `POST /api/checkout/create`), and a `window.open` after an `await` is no longer
 * inside the user gesture, so popup blockers refuse it. So a new tab opens a **store page that runs
 * this script, carrying a marker**, and the script there performs the handoff on load:
 *
 * - The marker page is the current page — same origin, so it loads our script and sends the same
 *   cart cookie — with a fragment: `#peak-checkout`, or `#peak-checkout=<encoded path+query>` when
 *   the opened control was a checkout link. A fragment never reaches Shopify's servers, caches or
 *   a `Referer` header.
 * - The carried link is what the D204 rules need on the new page: the `/cart/c/{token}` token to
 *   compare with `cart.js`, and the link itself for a store that turns out to be switched off. On
 *   read it must pass the same same-origin, checkout-shaped check as an intercepted anchor
 *   (`isCheckoutHref`); anything else is dropped and such a store's destination is `/checkout`.
 *
 * Pure and DOM-free so it is unit-testable: `tests/unit/storefront/new-tab.test.ts`.
 */

import { isCheckoutHref } from './selectors';

/** The fragment marker, without `#`. Also the prefix of the carried-link form. */
export const PEAK_NEW_TAB_MARKER = 'peak-checkout';

/** `#peak-checkout` or `#peak-checkout=<encoded>`; group 1 is the encoded link. */
const MARKER_HASH = /^#peak-checkout(?:=(.*))?$/;

/** The slice of a mouse event {@link wantsNewTab} reads. */
export type PeakPointerLike = {
  button: number;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
};

/**
 * Does this mouse event ask for its link in a new tab or window? A middle click (button 1), or
 * a primary click with ⌘, Ctrl or Shift. Alt is a download (or, on macOS, ⌥-click "download
 * linked file") and is never one, with or without other modifiers.
 */
export function wantsNewTab(event: PeakPointerLike): boolean {
  if (event.altKey) return false;
  return (
    event.button === 1 || (event.button === 0 && (event.metaKey || event.ctrlKey || event.shiftKey))
  );
}

/**
 * The marker URL for the current page: its path and query (never its own fragment) plus the
 * marker, with the checkout link's path and query carried when there is one. Root-relative, so
 * it can go straight into an `href` or `window.open`.
 *
 * `link` is absolute and same-origin (`checkoutLinkOf` in `./peak-checkout.ts`), so only its
 * path and query are kept; the origin is the page's own.
 */
export function newTabMarkerUrl(
  page: { pathname: string; search: string },
  link: string | null,
): string {
  let carried = '';
  if (link !== null) {
    try {
      const url = new URL(link);
      carried = '=' + encodeURIComponent(url.pathname + url.search);
    } catch {
      // An unparsable link is not carried; the new tab then hands off without it.
    }
  }
  return page.pathname + page.search + '#' + PEAK_NEW_TAB_MARKER + carried;
}

/**
 * The marker in this page's fragment: `null` when there is none, else `{ link }` with the
 * carried checkout link resolved and absolute — or `null` when nothing was carried or what was
 * carried is not a same-origin checkout link (`isCheckoutHref`), which is never trusted.
 */
export function readNewTabMarker(hash: string, pageOrigin: string): { link: string | null } | null {
  const match = MARKER_HASH.exec(hash);
  if (match === null) return null;
  let link: string | null = null;
  try {
    const raw = match[1] ? decodeURIComponent(match[1]) : null;
    if (isCheckoutHref(raw, pageOrigin)) link = new URL(raw as string, pageOrigin).href;
  } catch {
    // A malformed escape: a bare marker.
  }
  return { link };
}
